import { useCallback, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'

// Menu engineering AI brief — generate-on-call, no DB cache.
//
// Phases: 'idle' | 'generating' | 'ready' | 'error'
//
// The edge fn returns a tiny_data short-circuit response when the
// restaurant has < 10 recipe_components. We surface that as the
// tinyData flag so the card can render a setup nudge instead of
// the full brief block.
export function useMenuEngineeringBrief() {
  const [brief, setBrief] = useState(null)
  const [phase, setPhase] = useState('idle')
  const [error, setError] = useState(null)

  // Multi-unit scope. Grouped operators carry the selected unit so the
  // edge fn scopes the brief to that one restaurant (the verified-param
  // path in _shared/auth.ts re-targets it). Single-unit (null/undefined)
  // sends {} — byte-identical legacy behavior, the fn falls back to the
  // caller's first membership id.
  const { currentRestaurantId } = useCurrentUnit()

  const generate = useCallback(async () => {
    if (phase === 'generating') return
    // M1 — clear any prior brief BEFORE the request. If regeneration
    // fails or is rate-limited, the parent (WeeklyInsightsScreen) sees
    // brief=null via onBriefGenerated instead of holding onto a stale
    // result. The error message tells the user the previous run is
    // gone; they re-generate when they're ready.
    setBrief(null)
    setPhase('generating')
    setError(null)
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/generate-menu-engineering-brief`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify(currentRestaurantId ? { restaurant_id: currentRestaurantId } : {}),
      })
      // M4 — friendly handling of the per-user 10-minute rate limit.
      if (resp.status === 429) {
        setError('You can generate a new brief in a few minutes. The most recent one is still on screen.')
        setPhase('error')
        return null
      }
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data.error) {
        setError(data.message || 'AI could not generate the brief.')
        setPhase('error')
        return null
      }
      setBrief(data)
      setPhase('ready')
      return data
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useMenuEngineeringBrief] generate failed', e)
      setError(`Could not generate: ${e?.message || 'try again.'}`)
      setPhase('error')
      return null
    }
  }, [phase, currentRestaurantId])

  return {
    brief,
    phase,
    error,
    generate,
    tinyData: !!brief?.tiny_data,
  }
}
